import {
  Component,
  forwardRef,
  input,
  output,
  signal,
  computed,
  ChangeDetectionStrategy,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type SoSwitchColorStyle = '' | 'verde' | 'azul' | 'secondary';

@Component({
  selector: 'so-switch-ui',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './so-switch.component.html',
  styleUrl: './so-switch.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SoSwitchUI),
      multi: true
    }
  ]
})
export class SoSwitchUI implements ControlValueAccessor {
  // Modern signal-based inputs
  label = input<string>('');
  subtitle = input<string>('');
  disabled = input<boolean>(false);
  width = input<number>(48);
  height = input<number>(28);
  labelOnLeft = input<boolean>(false);
  expanded = input<boolean>(false);
  activeColor = input<string>('');
  inactiveColor = input<string>('');
  colorStyle = input<SoSwitchColorStyle>('');

  // Backward compatibility inputs (aliases from old component)
  labels = input<boolean>(false);
  labelLeft = input<string>('');
  labelRight = input<string>('');
  isChecked = input<boolean | number>(false);
  controlled = input<boolean>(false);
  isDisabled = input<boolean>(false);
  customStyleOption = input<SoSwitchColorStyle>('');
  labelLeftStyle = input<Record<string, string>>({});
  labelRightStyle = input<Record<string, string>>({});
  divStyle = input<string>('');
  size = input<string>('25');
  invertLabelsWeight = input<boolean>(false);
  tooltip = input<string>('');
  customStyleFont = input<string>('');

  // Outputs
  statusChecked = output<boolean>();
  valueChanged = output<boolean>();

  // Internal state
  protected internalValue = signal<boolean>(false);

  // ControlValueAccessor callbacks
  private onChange: (value: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  // Computed values
  protected effectiveDisabled = computed(() => this.disabled() || this.isDisabled());
  
  protected effectiveLabel = computed(() => this.label() || '');
  
  protected effectiveColorStyle = computed(() => this.colorStyle() || this.customStyleOption() || 'secondary');

  protected hasLabels = computed(() => this.labels() || !!this.label() || !!this.subtitle());

  protected showLeftLabel = computed(() => this.labels() && !!this.labelLeft());
  
  protected showRightLabel = computed(() => this.labels() && !!this.labelRight());

  protected leftLabelClass = computed(() => {
    const checked = this.internalValue();
    const invert = this.invertLabelsWeight();
    if (!invert) {
      return checked ? '' : 'fw-bold';
    }
    return checked ? 'fw-bold' : '';
  });

  protected rightLabelClass = computed(() => {
    const checked = this.internalValue();
    const invert = this.invertLabelsWeight();
    if (!invert) {
      return checked ? 'fw-bold' : '';
    }
    return checked ? '' : 'fw-bold';
  });

  protected trackColor = computed(() => {
    if (this.effectiveDisabled()) {
      return 'var(--so-slate-200, #e2e8f0)';
    }
    
    const style = this.effectiveColorStyle();
    const isOn = this.internalValue();
    
    if (!isOn) {
      return this.inactiveColor() || 'var(--so-slate-300, #cbd5e1)';
    }

    if (this.activeColor()) {
      return this.activeColor();
    }

    switch (style) {
      case 'verde':
        return 'var(--so-verde, #A3BD36)';
      case 'azul':
        return 'var(--so-azul, #002A7F)';
      case 'secondary':
      default:
        return 'var(--so-secondary, #A3BD36)';
    }
  });

  protected thumbColor = computed(() => {
    if (this.effectiveDisabled()) {
      return 'var(--so-slate-400, #94a3b8)';
    }
    return '#ffffff';
  });

  protected thumbSize = computed(() => this.height() - 6);

  protected thumbPosition = computed(() => {
    const isOn = this.internalValue();
    const thumbSize = this.thumbSize();
    return isOn ? this.width() - thumbSize - 3 : 3;
  });

  protected switchWidth = computed(() => `${this.width()}px`);
  
  protected switchHeight = computed(() => `${this.height()}px`);
  
  protected borderRadius = computed(() => `${this.height() / 2}px`);

  protected boxShadow = computed(() => {
    if (this.internalValue() && !this.effectiveDisabled()) {
      const color = this.trackColor();
      return `0 2px 8px ${color}4d`;
    }
    return 'none';
  });

  constructor() {
    // Sync isChecked input with internal value
    effect(() => {
      const checked = this.isChecked();
      if (checked !== undefined) {
        this.internalValue.set(!!checked);
      }
    }, {  });
  }

  // ControlValueAccessor implementation
  writeValue(value: boolean | null): void {
    this.internalValue.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled via input signal
  }

  // Event handlers
  onToggle(): void {
    if (this.effectiveDisabled()) return;

    const newValue = !this.internalValue();
    if (!this.controlled()) {
      this.internalValue.set(newValue);
    }
    this.onChange(newValue);
    this.onTouched();
    this.statusChecked.emit(newValue);
    this.valueChanged.emit(newValue);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.onToggle();
    }
  }
}
